import { parsePhoneNumber, AsYouType, getCountryCallingCode } from 'libphonenumber-js'

export default class PhoneNumberComponent extends window.HTMLElement {

	constructor () {
		super();
	}

	connectedCallback() {
		this.debug = this.hasAttribute("debug");
		this.inputMasking = this.hasAttribute("enable-formatting");
		this.extension = this.hasAttribute("include-extension");
		this.dataset.countrycode = this.dataset.countrycode || 'US';
		this.countryCallingCode = getCountryCallingCode(this.dataset.countrycode);
		this.readOnlyPrefix = "+" + this.countryCallingCode;
		this.asYouType = new AsYouType(this.dataset.countrycode)

		const extensionDiv = `
			${this.extension ? `
				<div class="extension-part input-container">
					<strong>Ext.</strong>
					<input type="number" min="0" maxlength="4" id="${this.id}_phone_extension" name="${this.id}_phone_extension" />
				</div>
				` : ''}
		`.trim();

		const phoneInputElement =
			`<input class="main-input input-container" type="tel"
							id="${this.id}_input" name="${this.id}_input"
							placeholder="${this.getAttribute('placeholder')}"
							style="${this.inputMasking?"border:none;margin-bottom:0px;padding:0px;":""}"
			/>`

		let countryCallingCodeSpan = ''
		if (this.inputMasking) {
			countryCallingCodeSpan = `<span class="read-only-prefix">${this.readOnlyPrefix}</span>`
		}

		const phoneInputWrapperDiv =
		`<div class="input-wrapper">
			${countryCallingCodeSpan}
			${phoneInputElement}
		</div>`

		this.innerHTML = `
			<style>${require('../../sass/components/phone-number.scss').toString()}</style>
			<span class="error-message">${this.dataset.errormessage}</span>
			<div class="phone-component" >
				${this.inputMasking ? phoneInputWrapperDiv : phoneInputElement}
				${this.debug ? `<b id="${this.id}_parsed_number" class="phone_debug"></b>` : ''}
				${extensionDiv}
			</div>`;


		const phoneInputDom = this.getPhoneInput();
		const extInputDom = this.getExtInput();

		if (phoneInputDom) {
			phoneInputDom.addEventListener("keyup", (event) => {
				this.processPhone(event.target.value);
				this.checkErrors();
			});
		}

		if (extInputDom) {
			extInputDom.addEventListener("keyup", (event) => {
				this.setAttribute("extension", event.target.value);
				this.checkErrors();
			});
		}
		if (this.hasAttribute("value") && phoneInputDom) {
			this.processPhone(PhoneNumberComponent.toNationalNumber(this.getAttribute("value")));
		} else {
			this.processPhone("")
		}

		if (this.hasAttribute("extension") && extInputDom) {
			extInputDom.value = this.getAttribute("extension");
		}

		this.checkErrors();
	}

	/**
	 * @param {string} e164Number EG. "+16513871059"
	 * @return {string} full numeric phone number, but with country code stripped:
	 * 					EG. "6513871059"
	 */
	static toNationalNumber(e164Number) {
		if (!e164Number.length) {
			return e164Number;
		}

		try {
			return parsePhoneNumber(e164Number).nationalNumber;
		} catch (error) {
			console.error('Failed to parse national number from  ' + e164Number, error);
			return e164Number;
		}
	}

	/**
	 * Method responsible for updating the views attributes and scope.
	 * Will set the `value` attribute on the main component to the value of the this.parsedPhoneNumber.
	 * Will also clear the attribute in case the phone number is invalid.
 	 */
	setTagValues() {
		if (this.parsedPhoneNumber === null) {
			this.setValue('');
			return;
		}
		if (this.debug) {
			const debugValue =
				this.parsedPhoneNumber.number +
				(this.parsedPhoneNumber.ext ? " | " +
				this.parsedPhoneNumber.ext : "");
			this.querySelector(`#${this.id}_parsed_number`).innerText = debugValue;
		}
		if (this.isPhoneValid()) {
			this.setValue('');
		} else {
			this.setValue(this.parsedPhoneNumber.number);
		}
	}

	/**
	 * @param {string} phoneNumber User entered phone number to be parsed into an
	 * 								E.164 phone format based and set into
	 * 								this.parsedPhoneNumber
	 */
	parseNum(phoneNumber) {
		try {
			this.parsedPhoneNumber = parsePhoneNumber(phoneNumber, this.dataset.countrycode);
		} catch (error) {
			if (this.debug) {
				console.error(error);
			}
			this.parsedPhoneNumber = null;
		}
	}

	/**
	 * Validates if extension or phone are invalid and sets an error on the component.
	 */
	checkErrors() {
		if((this.extension && this.isExtValid()) || this.isPhoneValid()) {
			this.addError();
		} else {
			this.removeError();
		}
	}

	/**
	 * @return boolean if the user-entered phone number is invalid
	 */
	isPhoneValid() {
		const phoneInputDom = this.getPhoneInput();
		const userEnteredPhone = phoneInputDom.value;

		if (this.parsedPhoneNumber == null && userEnteredPhone != null && userEnteredPhone.trim() !== '') {
			return true;
		}

		if (this.parsedPhoneNumber) {
			const type = this.parsedPhoneNumber.getType();
			const countryMatches = type === 'TOLL_FREE' || this.parsedPhoneNumber.country === this.dataset.countrycode || ("US" === this.dataset.countrycode && "PR" === this.parsedPhoneNumber.country);
			return !(this.parsedPhoneNumber.isValid() && countryMatches);
		}
	}

	/**
	 * @return boolean if the user-entered phone extension is invalid
	 */
	isExtValid() {
		if (!this.extension) {
			return true;
		}
		const phoneInputDom = this.getPhoneInput();
		const extInputDom = this.getExtInput();

		return phoneInputDom && extInputDom && extInputDom.value !== "" && phoneInputDom.value === "";
	}

	/**
	 * Masks the user input into a localized phone number, if the input ends in a
	 * special character (part of the localization)
	 * e.g. "(722)555-" or "(722)" it will strip out this characters to prevent
	 * weird behaviour when backspacing.
	 *
	 * @param {string} phone string input from user
	 */
	setPhoneInputMasked(phone) {
		this.asYouType.reset();
		let inputMasked = this.asYouType.input(phone);
		// if they've backspaced to a special character, backspace them again
		if (isNaN(inputMasked[inputMasked.length-1])) {
			inputMasked = inputMasked.substring(0, inputMasked.length-1);
		}
		const phoneInput = this.getPhoneInput();
		phoneInput.value = inputMasked;
	}

	/**
	 * User entered phone number input that has to be parsed and processed into
	 * the component view
	 * @param {string} phone string input from user
	 */
	processPhone(phone) {
		if (phone.length) {
			this.parseNum(phone);
			this.setTagValues();
		} else {
			this.setValue('');
		}
		if (this.inputMasking) {
			this.setPhoneInputMasked(phone);
		}
	}

	/**
	 * Adds error class to the main component.
	 */
	addError() {
		this.classList.add('error');
	}

	/**
	 * Removes error class to the main component.
	 */
	removeError() {
		this.classList.remove('error');
	}

	/**
	 * @return {HTMLInputElement} Returns the input DOM element used to get
	 *														user-entered phone number.
	 */
	getPhoneInput() {
		return this.querySelector('input');
	}

	/**
	 * @return {HTMLInputElement} Returns the input DOM element used to get
	 *														user-entered phone extension.
	 */
	getExtInput() {
		return this.querySelector('input[type=number]');
	}

	/**
	 * Sets the value of this component (as oppossed to the value of any input
   * elements this component is composed of).
	 * For backwards compatibility, value is stored redundantly on an attribute
	 * named `data-numberResult`
	 * @param {string} newValue The new value to set this component's value to
	 */
	setValue(newValue) {
		this.setAttribute('value', newValue);
		this.setAttribute('data-numberResult', newValue);
	}
}



// WEBPACK FOOTER //
// ./src/js/components/phone.js